// src/public-routes/sectors/TravelSector.jsx
import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import {
    FaPlane,
    FaSearch,
    FaGlobe,
    FaCalendarCheck,
    FaUsers,
    FaChartLine,
    FaStar,
    FaTrophy
} from 'react-icons/fa';

const TravelSector = () => {
    useEffect(() => {
        window.scrollTo(0, 0);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const stats = [
        { number: '74%', label: 'of travelers research online before booking' },
        { number: '3.2x', label: 'higher engagement with personalized travel content' },
        { number: '52%', label: 'of travel bookings are influenced by digital marketing' },
        { number: '68%', label: 'increase in direct bookings with optimized campaigns' }
    ];

    const approaches = [
        {
            title: "Destination Marketing",
            description: "Showcase destinations and experiences through compelling content, SEO, and paid campaigns that inspire travelers and drive bookings.",
            icon: FaGlobe
        },
        {
            title: "Seasonal Campaign Strategy",
            description: "Maximize bookings during peak seasons and drive demand during off-peak periods with targeted, timely marketing campaigns.",
            icon: FaCalendarCheck
        },
        {
            title: "Digital Journey Optimization",
            description: "Create seamless digital experiences across all touchpoints that guide travelers from inspiration to booking confirmation.",
            icon: FaSearch
        },
        {
            title: "Customer Experience Marketing",
            description: "Build lasting relationships with travelers through personalized communication, loyalty programs, and post-booking engagement.",
            icon: FaUsers
        }
    ];

    const benefits = [
        ['Increased Direct Bookings', 'Drive more reservations through your own channels and reduce dependency on OTAs'],
        ['Stronger Brand Presence', 'Stand out in a crowded, experience-driven market with distinctive digital marketing'],
        ['Higher Customer Loyalty', 'Build lasting relationships that encourage repeat bookings and referrals'],
        ['Seasonal Revenue Optimization', 'Balance demand throughout the year with strategic campaign timing'],
        ['Competitive Differentiation', 'Showcase unique experiences that set your travel brand apart from competitors']
    ];

    const questions = [
        'How can you stand out in a crowded, experience-driven travel market?',
        'What strategies will inspire travelers and convert them into bookings?',
        'How do you create seamless digital journeys that drive conversions?',
        'Which channels deliver the highest ROI for travel brands?',
        'How can you build traveler loyalty in a competitive market?'
    ];

    return (
        <div className="st-page">
            {/* Top Image Section */}
            <div className="st-decorative-image-container">
                <img
                    src={require('./Travel.PNG')}
                    alt="Travel Digital Marketing - Inspire and Convert Travelers"
                    loading="eager"
                />
            </div>

            {/* Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Travel</span> Digital Marketing
                    </h1>
                    <p>Stand out in a crowded, experience-driven market by creating seamless digital journeys that inspire, engage, and convert</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    In the highly competitive <span className="sv-keyword-highlight">travel industry</span>, we help brands stand out in a crowded, experience-driven market by creating seamless digital journeys that inspire, engage, and convert.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>travel digital marketing services</strong> combine strategic thinking, data intelligence, and advanced tools to create campaigns that connect the right audience with the right message at the right moment. We understand what drives travelers to book.
                                </p>
                                <p className="sv-description">
                                    We understand the unique challenges travel brands face: seasonal demand fluctuations, intense competition, the need for inspiring content that converts, and creating seamless booking experiences that turn interest into confirmed reservations.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Travel Strategy
                                    </Button>
                                    <Button tag={Link} to="/contact" color="dark">
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">Travel Marketing</span> Matters
                        </h2>
                        <Row className="sv-stats-grid">
                            {stats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* What We Do Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Do for Travel Brands</h2>
                                <p className="sv-section-description">
                                    <strong>Travel digital marketing</strong> goes beyond just promoting destinations. It's about creating inspiring digital experiences that capture the imagination of travelers, build trust, and seamlessly guide them from dreaming to booking.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions We Answer:</h4>
                                    {questions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Overview */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Invest in Travel Marketing?</h2>
                                <p className="sv-section-description">
                                    The travel industry is more competitive than ever. A comprehensive digital marketing strategy is essential to inspire travelers, stand out from competitors, and create booking experiences that convert interest into confirmed reservations.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-globe"></i>
                                        <h5>Global Reach</h5>
                                        <p>Connect with travelers worldwide through targeted digital campaigns</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-star"></i>
                                        <h5>Brand Inspiration</h5>
                                        <p>Create compelling content that inspires travelers to choose your brand</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-calendar-check"></i>
                                        <h5>Booking Conversion</h5>
                                        <p>Turn interest into confirmed bookings with optimized digital journeys</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-users"></i>
                                        <h5>Traveler Loyalty</h5>
                                        <p>Build lasting relationships that drive repeat bookings and referrals</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Approach Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Travel Marketing Services</h2>
                                <p className="sv-section-description">
                                    Reachtix offers a comprehensive suite of travel marketing services designed to inspire travelers, drive bookings, and build lasting relationships that generate repeat business.
                                </p>

                                <Row className="sv-services-grid">
                                    {approaches.map((service, index) => (
                                        <Col lg={6} md={6} key={index} className="mb-4">
                                            <div className="sv-approach-card p-4 h-100">
                                                <div className="sv-approach-icon">
                                                    <service.icon />
                                                </div>
                                                <div className="sv-approach-content">
                                                    <h4>{service.title}</h4>
                                                    <p className="service-description">{service.description}</p>
                                                </div>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Table Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of Travel Marketing With Reachtix</h2>
                                <div className="table-responsive">
                                    <table className="table sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Core Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Main CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Transform Your Travel Brand?</h2>
                                <p className="sv-cta-description">
                                    Whether you're a tour operator, travel agency, airline, or destination marketer, our comprehensive travel marketing services provide the expertise and results you need to inspire travelers and drive bookings.
                                </p>
                                <p className="sv-cta-description">
                                    Let's create digital journeys that capture the imagination and turn dreamers into booked travelers.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button tag={Link} to="/contact" color="primary">
                                        <i className="fas fa-calendar-check mr-2"></i>
                                        Start Travel Consultation
                                    </Button>
                                    <Button tag={Link} to="/contact" color="light">
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get Marketing Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TravelSector;